import { useState } from "react";
import { toast } from "sonner";
import { Languages, CheckCircle2 } from "lucide-react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../ui/alert-dialog";
import { api, errMsg, has } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Md } from "../../pages/Analyst";
import { streamSse } from "../../pages/PpcDrafts";

export const PpcCebuano = ({ d, onChanged }) => {
  const { user } = useAuth();
  const [content, setContent] = useState(d.ceb_content || "");
  const [live, setLive] = useState("");
  const [busy, setBusy] = useState(false);
  const status = d.ceb_status;
  const url = `/analyst/ppc-drafts/${d.draft_id}/cebuano`;
  const generate = async () => {
    setBusy(true); setLive("");
    let ok = false;
    try {
      await streamSse(url, {}, (ev) => {
        if (ev.type === "delta") setLive((t) => t + ev.text);
        if (ev.type === "error") toast.error(ev.message);
        if (ev.type === "done") ok = true;
      });
      if (ok) { toast.success("Cebuano version ready for review"); onChanged(); }
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); setLive(""); }
  };
  const save = async () => { try { await api.patch(url, { content }); toast.success("Saved"); onChanged(); } catch (e) { toast.error(errMsg(e)); } };
  const approve = async () => { try { await api.patch(url, { content }); await api.post(`${url}/approve`); toast.success("Cebuano version approved for GKK leaders"); onChanged(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div className="space-y-3 rounded-xl border border-amber-700/15 bg-[#FBF8F2] p-4" data-testid="ceb-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 font-semibold text-stone-800"><Languages className="h-4 w-4 text-amber-900" />Cebuano version for GKK leaders</h3>
        {status === "approved" && <span className="flex items-center gap-1 rounded-full bg-[#285E3B] px-3 py-1 text-xs font-semibold text-white" data-testid="ceb-status"><CheckCircle2 className="h-3 w-3" />Approved by {d.ceb_approved_by} · visible to GKK leaders</span>}
        {status === "draft" && <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-900" data-testid="ceb-status">Draft · not yet visible to GKK leaders</span>}
      </div>
      {busy && <div className="rounded-lg bg-white p-4" data-testid="ceb-live">{live ? <Md text={live} /> : <p className="text-sm italic text-stone-500">Translating the approved summary…</p>}</div>}
      {!busy && !status && <p className="text-sm text-stone-600">The Analyst translates the approved English summary. Administrators check and edit it; it reaches GKK leaders only after the Parish Priest approves it.</p>}
      {!busy && status && (
        <>
          {d.ceb_edited && <p className="text-xs text-stone-500" data-testid="ceb-edited">Edited by people since the AI translation{d.ceb_last_edited_by ? ` (last: ${d.ceb_last_edited_by})` : ""}</p>}
          <Tabs defaultValue={status === "draft" ? "edit" : "preview"}>
            <TabsList className="bg-[#F5F0E6]"><TabsTrigger value="edit" data-testid="ceb-tab-edit">Edit</TabsTrigger><TabsTrigger value="preview" data-testid="ceb-tab-preview">Preview</TabsTrigger></TabsList>
            <TabsContent value="edit"><Textarea rows={16} value={content} disabled={status !== "draft"} onChange={(e) => setContent(e.target.value)} className="bg-white font-mono-data text-xs" data-testid="ceb-content-input" /></TabsContent>
            <TabsContent value="preview"><div className="rounded-lg bg-white p-5"><Md text={content} /></div></TabsContent>
          </Tabs>
        </>
      )}
      {status !== "approved" && !busy && (
        <div className="flex flex-wrap gap-2">
          {!status && <Button variant="outline" onClick={generate} data-testid="ceb-generate-button"><Languages className="h-4 w-4 mr-1" />Prepare Cebuano version</Button>}
          {status === "draft" && (
            <AlertDialog>
              <AlertDialogTrigger asChild><Button variant="outline" data-testid="ceb-generate-button"><Languages className="h-4 w-4 mr-1" />Translate again</Button></AlertDialogTrigger>
              <AlertDialogContent className="bg-white">
                <AlertDialogHeader><AlertDialogTitle>Translate again?</AlertDialogTitle><AlertDialogDescription>The current Cebuano text{d.ceb_edited ? ", including people's edits," : ""} will be replaced by a new AI translation.</AlertDialogDescription></AlertDialogHeader>
                <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={generate} data-testid="ceb-regenerate-confirm">Translate again</AlertDialogAction></AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          {status === "draft" && <Button variant="outline" onClick={save} data-testid="ceb-save-button">Save changes</Button>}
          {status === "draft" && has(user, "priority:decide") && (
            <AlertDialog>
              <AlertDialogTrigger asChild><Button className="bg-[#285E3B] hover:bg-[#1E462C]" data-testid="ceb-approve-button">Approve for GKK leaders</Button></AlertDialogTrigger>
              <AlertDialogContent className="bg-white">
                <AlertDialogHeader><AlertDialogTitle>Approve the Cebuano version?</AlertDialogTitle><AlertDialogDescription>Please confirm it says the same as the approved English summary. GKK leaders will be able to read it after signing in, and the text will be locked.</AlertDialogDescription></AlertDialogHeader>
                <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={approve} data-testid="ceb-approve-confirm">Approve</AlertDialogAction></AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      )}
    </div>
  );
};
